"use client";

import { Switch as SwitchPrimitive } from "@base-ui/react/switch";
import type React from "react";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";

/** The switch face's state fills, shared with the menu and context-menu
 *  `variant="switch"` indicators so the faces cannot drift.
 *
 *  The unchecked track is a foreground ALPHA, not the opaque `input` token:
 *  dark `input` (26%) sits a hair off the card fill and the track disappeared
 *  on it. Surface-relative alpha stays visible on any host surface (card,
 *  popover, elevated) in both modes — and `--foreground` is surface-scoped
 *  (`data-on`), so the track solves against the surface it actually sits on.
 *
 *  A disabled CHECKED switch drops the primary track for a soft foreground
 *  alpha: "on, and not yours to change" (a section a type always shows, a
 *  setting held by policy) is a fact, not a control, and the full-ink bar at
 *  64% opacity still read as the loudest thing on the row. The thumb keeps
 *  its on-side position, so the state stays legible. */
export const SWITCH_TRACK_STATE_CLASS =
  "data-checked:bg-primary data-unchecked:bg-foreground/10 dark:data-unchecked:bg-foreground/20 data-disabled:data-checked:bg-foreground/30";

/** In dark mode the unchecked thumb takes a visible fill: bg-background on
 *  the dark track is a punched hole, which leaves the disabled dim nothing to
 *  read against. Checked keeps bg-background in both modes — the thumb must
 *  stay dark against the white checked track. `in-data-unchecked` reads the
 *  state off the enclosing root/indicator, which every host provides. */
export const SWITCH_THUMB_FILL_CLASS =
  "bg-background dark:in-data-unchecked:bg-foreground/85";

export function Switch({
  className,
  size,
  ...props
}: SwitchPrimitive.Root.Props & Omittable<{
  /** `sm` is the dense-row rung — a 14px pill on desktop (16px on mobile)
   *  for option lists whose rows sit on the 32px floor. Omit for the
   *  standalone form-control size. Every dimension derives from
   *  `--thumb-size`, so the rung is one variable override. */
  size?: "sm";
}>): React.ReactElement {
  return (
    <SwitchPrimitive.Root
      className={cn(
        "inline-flex h-[calc(var(--thumb-size)+2px)] w-[calc(var(--thumb-size)*2-2px)] shrink-0 cursor-pointer items-center rounded-full p-px outline-none transition-[background-color,box-shadow] duration-base [--thumb-size:--spacing(5)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background data-disabled:cursor-not-allowed data-disabled:opacity-64 sm:[--thumb-size:--spacing(4)]",
        SWITCH_TRACK_STATE_CLASS,
        size === "sm" &&
          "[--thumb-size:--spacing(4)] sm:[--thumb-size:--spacing(3)]",
        className,
      )}
      data-slot="switch"
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          "pointer-events-none block aspect-square h-full origin-left in-[[role=switch]:active,[data-slot=label]:active,[data-slot=field-label]:active]:not-data-disabled:scale-x-110 in-[[role=switch]:active,[data-slot=label]:active,[data-slot=field-label]:active]:rounded-[var(--thumb-size)/calc(var(--thumb-size)*1.1)] rounded-(--thumb-size) shadow-sm/5 will-change-transform [transition:translate_.15s,border-radius_.15s,scale_.1s_.1s,transform-origin_.15s,background-color_.15s] data-checked:origin-[var(--thumb-size)_50%] data-checked:translate-x-[calc(var(--thumb-size)-4px)]",
          SWITCH_THUMB_FILL_CLASS,
        )}
        data-slot="switch-thumb"
      />
    </SwitchPrimitive.Root>
  );
}
